import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// material import
import {
  Box,
  Typography,
  Grid2,
  Card,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Chip,
  Alert,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

// project import
import Header from "../../ui-components/header";
import Footer from "../../ui-components/footer";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toISODate } from "../../utils/dateUtils";
import { useSnackbar } from "../../ui-components/snackbarProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ErrorTwoToneIcon from "@mui/icons-material/ErrorTwoTone";

import { parsePhoneNumberFromString } from "libphonenumber-js";

// service import
import CalendarServices from "../../services/v1/calendarServices";
import DropdownServices from "../../services/v1/dropdownServices";
import CheckoutServices from "../../services/v1/checkoutServices";
import DateCalendarServerRequest from "../../ui-components/customCalendar";
import HelpFloatingButton from "../../ui-components/HelpFloatingButton";

const Index = () => {
  const location = useLocation();
  const { title, price, packageID } = location.state || {};
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [rows, setRows] = useState([]);
  const [recurringOption, setRecurringOption] = useState(2);
  const [recurringOptions, setRecurringOptions] = useState([]);
  const [frequencyOption, setFrequencyOption] = useState("");
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [titleOptions, setTitleOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedOption, setSelectedOption] = useState("paynow");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedDatePicker, setSelectedDatePicker] = useState(null);
  const [sameAsContact, setSameAsContact] = useState(false);
  const [isAvailblitityLoading, setIsAvailblitityLoading] = useState(false);
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState(false);
  const isPackageTwo = Number(rows[0]?.packageID ?? packageID) === 2;

  // New state variables for form inputs
  const [prfTitle, setPrfTitle] = useState("");
  const [fullName, setFullName] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [rawPhone, setRawPhone] = useState("");
  const [whatsApp, setWhatsApp] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  // validation
  const [formErrors, setFormErrors] = useState({
    prfTitle: false,
    fullName: false,
    country: false,
    phone: false,
    email: false,
    message: false,
    selectedDatePicker: false,
  });

  const [touched, setTouched] = useState({
    prfTitle: false,
    fullName: false,
    country: false,
    phone: false,
    email: false,
    message: false,
    selectedDatePicker: false,
  });

  const messages = {
    paynow: "Reserved the date and make the payment via Credit/ Debit card.",
    paylater:
      "Reserved the date now and make the payment later via Credit/ Debit card or directly into our bank account. Please use your Payment ID as the payment reference.",
  };
  const stepLabels = ["Date Selection", "Donor Details", "Donation Method"];

  // Advancing/going back a step re-renders the same scroll position the
  // "Continue"/"Back" button was clicked from, which can land the next
  // step's content off-screen (below the fold) instead of at its top.
  useEffect(() => {
    const element = document.getElementById("calendar-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeStep]);

  // fetch types, frequencies, countries, and payment methods
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const types = await CalendarServices.allTypes();
        setRecurringOptions(types.data || []);

        const frequencies = await CalendarServices.allFrequencies();
        setFrequencyOptions(frequencies.data || []);

        const countries = await DropdownServices.allCountries();
        setCountryOptions(countries.data || []);

        const titles = await DropdownServices.allTitles();
        setTitleOptions(titles.data || []);

        const paymentMethods = await CheckoutServices.getPaymentMethods();
        setPaymentMethods(paymentMethods.data || []);
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchOptions();
  }, []);

  // Availability check
  const handleCheckAvailability = async () => {
    let errors = {};

    setIsAvailblitityLoading(true);

    if (!selectedDate) {
      errors.selectedDate = "Please select a date for your booking first";
    }

    if (!recurringOption) {
      errors.recurringOption = "Please select a donation type: One-Time or Recurring.";
    }

    if (recurringOption === 1) {
      if (!frequencyOption) {
        errors.frequencyOption = "Please select a Frequency for Recurring";
      }
      if (!selectedDatePicker) {
        errors.selectedDatePicker = "Please select a date to end the recurring";
      }
    }

    // If there are errors, show the first error message
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0]; // Get the first error message
      showSnackbar(firstError, { severity: "error" });

      // Store errors in state for UI validation if needed
      setFormErrors(errors);
      setIsAvailblitityLoading(false);
      return;
    }

    // Skip availability check for one-time (lifetime) sponsorship
    if (recurringOption !== 1) {
      showSnackbar("Date selected successfully!", { severity: "success" });
      setIsAvailblitityLoading(false);
      return;
    }

    // Format dates (use Asia/Colombo ISO to avoid timezone shifts)
    const formattedDate = toISODate(dayjs(selectedDate).format("YYYY-MM-DD"));
    const formattedToDate = selectedDatePicker
      ? toISODate(dayjs(selectedDatePicker).format("YYYY-MM-DD"))
      : null;

    try {
      const response = await CalendarServices.checkAvailability(
        formattedDate,
        formattedToDate,
        recurringOption,
        frequencyOption,
      );
      showSnackbar(response.message, { severity: "success" });
    } catch (error) {
      showSnackbar(error.response?.data?.message || error.message, {
        severity: "error",
      });
    } finally {
      setIsAvailblitityLoading(false);
    }
  };

  const handleStep1Next = async () => {
    await handleCheckAvailability();

    const hasStep1Errors =
      !selectedDate ||
      !recurringOption ||
      (recurringOption === 1 && (!frequencyOption || !selectedDatePicker));

    if (!hasStep1Errors) {
      setActiveStep(1);
    }
  };

  const handleStep2Next = () => {
    const baseErrors = {
      prfTitle: !prfTitle,
      fullName: !fullName,
      country: !country,
      phone: !phone,
      email: !email.trim()
        ? "This field is required"
        : !validateEmail(email.trim())
          ? "Invalid email format"
          : "",
      message: !message,
    };

    const errors = {
      ...baseErrors,
      ...(recurringOption === 1 && { selectedDatePicker: !selectedDatePicker }),
    };

    const countryCode =
      countryOptions.find((option) => option.country === country)?.iso || "US";

    if (phone && !validatePhoneNumber(phone, countryCode)) {
      errors.phone = true;
    }

    setFormErrors(errors);
    setTouched({
      prfTitle: true,
      fullName: true,
      country: true,
      phone: true,
      email: true,
      message: true,
      selectedDatePicker: recurringOption === 1,
    });

    if (Object.values(errors).some(Boolean)) {
      showSnackbar("Please fill all the required fields!", {
        severity: "error",
      });
      return;
    }

    setActiveStep(2);
  };

  const handleStepBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepForwardFromPayment = async () => {
    await handleReserve();
  };

  //create reservation
  const handleReserve = async () => {
    setIsSubmitBtnLoading(true);
    const baseFields = {
      prfTitle: true,
      fullName: true,
      country: true,
      phone: true,
      email: true,
      message: true,
    };

    setTouched({
      ...baseFields,
      ...(recurringOption === 1 && { selectedDatePicker: true }),
    });

    // Validate all required fields

    const baseErrors = {
      prfTitle: !prfTitle,
      fullName: !fullName,
      country: !country,
      phone: !phone,
      email: !email,
      message: !message,
    };

    const errors = {
      ...baseErrors,
      ...(recurringOption === 1 && { selectedDatePicker: !selectedDatePicker }),
    };

    // compute country code for phone validation
    const countryCode =
      countryOptions.find((option) => option.country === country)?.code || "US";

    // add actual phone/email validation on top of empty check
    if (phone && !validatePhoneNumber(phone, countryCode)) {
      errors.phone = true;
    }
    if (email && !validateEmail(email)) {
      errors.email = true;
    }

    setFormErrors(errors);

    if (
      Object.values(errors).some((error) => error) ||
      !selectedDate ||
      !recurringOption ||
      !selectedOption
    ) {
      showSnackbar("Please fill all the required fields!", {
        severity: "error",
      });
      return;
    }

    const reservationData = {
      packageID,
      date: selectedDate
        ? toISODate(dayjs(selectedDate).format("YYYY-MM-DD"))
        : null,
      toDate: selectedDatePicker
        ? toISODate(dayjs(selectedDatePicker).format("YYYY-MM-DD"))
        : null,
      typeID: recurringOption,
      frequencyID: frequencyOption || (recurringOption !== 1 ? "none" : null),
      prof_titleID:
        titleOptions.find((option) => option.title === prfTitle)?.id || "",
      fullname: fullName,
      countryID:
        countryOptions.find((option) => option.country === country)?.id || "",
      city: city,
      contactNo: phone,
      whatsappNo: whatsApp || null,
      email,
      message: message,
      paymentMethodID: parseInt(selectedOption),
    };
    try {
      const response =
        await CheckoutServices.createReservation(reservationData);
      const reservationDetails = response.data.data;
      showSnackbar(response.message, {
        severity: "success",
      });

      if (response.data.status === "success") {
        const nextRoute =
          selectedOption === "1" ? "/order-pay" : "/paylater-confirmation";
        navigate(nextRoute, { state: { reservationDetails } });
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || error.message, {
        severity: "error",
      });
    } finally {
      setIsSubmitBtnLoading(false);
    }
  };

  useEffect(() => {
    if (title && price && packageID) {
      setRows([
        {
          packageName: title,
          price: price,
          packageID: packageID,
        },
      ]);
    }
  }, [title, price, packageID]);

  useEffect(() => {
    if (isPackageTwo) {
      setRecurringOption(2);
      setFrequencyOption("");
    }
  }, [isPackageTwo]);

  // automatic scroller
  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  // Update the date column in the table
  const handleDateChange = (date) => {
    const formattedDate = toISODate(dayjs(date).format("YYYY-MM-DD"));
    setSelectedDate(date);

    setRows((prevRows) =>
      prevRows.map((row, index) =>
        index === 0 ? { ...row, selectedDate: formattedDate } : row,
      ),
    );
  };

  // Update the type field in the table
  const handleRecurringOptionChange = (optionId) => {
    const selectedOption = recurringOptions.find(
      (option) => option.id === optionId,
    );
    const label = selectedOption ? selectedOption.type : "N/A";

    setRecurringOption(optionId);

    setRows((prevRows) =>
      prevRows.map((row, index) =>
        index === 0 ? { ...row, recurringOption: label } : row,
      ),
    );
  };

  // Update the From-To fields
  // const handleDatePickerChange = (date) => {
  //   setSelectedDatePicker(date);
  //   setFormErrors((prev) => ({
  //     ...prev,
  //     selectedDatePicker: !date,
  //   }));
  //   setRows((prevRows) =>
  //     prevRows.map((row, index) =>
  //       index === 0
  //         ? { ...row, selectedDatePicker: dayjs(date).format("YYYY-MM-DD") }
  //         : row
  //     )
  //   );
  // };

  const handleDatePickerChange = (date) => {
    if (!date) return;

    const baseDay = dayjs(selectedDate).date(); // Get day from DateCalendarServerRequest
    const maxDayInMonth = dayjs(date).endOf("month").date(); // Ensure valid day
    const safeDay = Math.min(baseDay, maxDayInMonth); // Prevent overflow like Feb 30

    const adjustedDate = dayjs(date).date(safeDay); // Apply the preserved day

    setSelectedDatePicker(adjustedDate);
    setFormErrors((prev) => ({
      ...prev,
      selectedDatePicker: !adjustedDate,
    }));
    setRows((prevRows) =>
      prevRows.map((row, index) =>
        index === 0
          ? {
              ...row,
              selectedDatePicker: toISODate(adjustedDate.format("YYYY-MM-DD")),
            }
          : row,
      ),
    );
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const generateDates = (startDate, endDate, frequency) => {
    const dates = [];
    let currentDate = dayjs(startDate);
    const end = dayjs(endDate);

    while (currentDate.isBefore(end) || currentDate.isSame(end)) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate =
        frequency === 1
          ? currentDate.add(1, "month")
          : currentDate.add(1, "year");
    }

    return dates;
  };

  const renderDates = () => {
    if (recurringOption === 1) {
      const dates = generateDates(
        selectedDate,
        selectedDatePicker,
        frequencyOption,
      );
      return dates.map((date, index) => <div key={index}>{date}</div>);
    } else {
      return <div>{selectedDate}</div>;
    }
  };

  const validatePhoneNumber = (number, countryCode) => {
    if (!number) return false;
    try {
      const phoneNumber = parsePhoneNumberFromString(number, countryCode);
      return phoneNumber?.isValid() || false;
    } catch (error) {
      return false;
    }
  };

  // Helper function to prepend country code
  // Helper
  const prependCountryCodeSafe = (raw, code) => {
    if (!raw) return code;
    let cleaned = raw.replace(/^0+/, "").trim();

    // Remove previous country code if present
    if (cleaned.startsWith("+")) {
      cleaned = cleaned.replace(/^\+\d+\s*/, "");
    }

    return `${code} ${cleaned}`;
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const selectedRecurringType = recurringOptions.find(
    (option) => option.id === recurringOption,
  )?.type;

  return (
    <Box>
      <Header title="RESERVE YOUR DATE TODAY" />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: { xs: "auto", md: "100%" },
            py: 2,
            px: 2,
            mt: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ width: { xs: "auto", md: "70%" } }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h5"
                sx={{
                  display: { xs: "block", sm: "none" },
                  color: "#8B2757",
                  fontWeight: "bold",
                  fontSize: 23,
                  letterSpacing: 2,
                  mb: 3,
                }}
              >
                RESERVE YOUR DATE TODAY
              </Typography>

              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  justifyContent: "center",
                  "& .MuiStepIcon-root.Mui-active": { color: "#8B2757" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#8B2757" },
                  "& .MuiStepLabel-root .MuiStepLabel-label": {
                    color: "#8B2757",
                  },
                }}
              >
                {stepLabels.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <LinearProgress
                variant="determinate"
                value={((activeStep + 1) / stepLabels.length) * 100}
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 999,
                  bgcolor: "rgba(141,39,87,0.12)",
                  "& .MuiLinearProgress-bar": { bgcolor: "#8B2757" },
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            px: 2,
            pb: 5,
          }}
        >
          <Box sx={{ width: { xs: "100%", md: "70%" } }}>
            <Grid2 item size={{ md: 8.2, xs: 10 }} mt={7} id="calendar-section">
              <Card align="center">
                <Grid2 container spacing={2} justifyContent="center">
                  {activeStep === 0 && (
                    <>
                      <Grid2 item size={{ xs: 12, md: 6 }}>
                        <DateCalendarServerRequest
                          value={selectedDate}
                          onDateChange={(date) => handleDateChange(dayjs(date))}
                        />

                        <Box sx={{ mt: 1, textAlign: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", fontSize: 12 }}
                          >
                            Dates shown are in Sri Lanka time (Asia/Colombo).
                            All bookings use Sri Lanka calendar day.
                          </Typography>
                        </Box>

                        {!isPackageTwo && (
                          <Grid2>
                            <ToggleButtonGroup
                              color="secondary"
                              exclusive
                              value={recurringOption}
                              onChange={(event, newValue) => {
                                handleRecurringOptionChange(newValue);
                              }}
                              aria-label="Type"
                              sx={{ mt: 0, mb: 3 }}
                            >
                              {recurringOptions.map((option) => (
                                <ToggleButton key={option.id} value={option.id}>
                                  {option.type}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>

                            {recurringOption === 1 && (
                              <ToggleButtonGroup
                                color="secondary"
                                exclusive
                                value={frequencyOption}
                                onChange={(event, newValue) =>
                                  setFrequencyOption(newValue)
                                }
                                aria-label="Frequency"
                                sx={{ ml: 2 }}
                              >
                                {frequencyOptions.map((option) => (
                                  <ToggleButton
                                    key={option.id}
                                    value={option.id}
                                  >
                                    {option.frequency}
                                  </ToggleButton>
                                ))}
                              </ToggleButtonGroup>
                            )}
                          </Grid2>
                        )}

                        <Grid2>
                          {recurringOption === 1 && (
                            <Box
                              sx={{
                                mt: { md: -1, xs: 2 },
                                mb: { md: 2, xs: 2 },
                              }}
                            >
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label="Recurring Ends"
                                  value={selectedDatePicker}
                                  onChange={handleDatePickerChange}
                                  views={
                                    frequencyOption === 1
                                      ? ["year", "month"]
                                      : ["year"]
                                  }
                                  openTo={
                                    frequencyOption === 1 ? "month" : "year"
                                  }
                                  slotProps={{
                                    textField: {
                                      error:
                                        touched.selectedDatePicker &&
                                        formErrors.selectedDatePicker,
                                      helperText:
                                        touched.selectedDatePicker &&
                                        formErrors.selectedDatePicker
                                          ? "This field is required"
                                          : "",
                                      onBlur: () =>
                                        handleBlur("selectedDatePicker"),
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                          )}
                        </Grid2>

                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 7,
                          }}
                        >
                          {recurringOption === 1 && (
                            <Button
                              variant="outlined"
                              sx={{
                                color: "#8B2757",
                                borderRadius: 1,
                                borderColor: "#8B2757",
                                textTransform: "none",
                                fontWeight: "bold",
                                maxWidth: 400,
                              }}
                              disabled={isAvailblitityLoading}
                              onClick={handleCheckAvailability}
                            >
                              Check Availability for Recurring
                            </Button>
                          )}
                        </Box>
                      </Grid2>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />
                      <Grid2 item size={{ xs: 12, md: 5, mr: -3 }}>
                        {rows.map((row, rowIndex) => (
                          <Grid2 key={rowIndex}>
                            <Box sx={{ mt: 5, ml: 3 }}>
                              <Typography
                                variant="h6"
                                sx={{ fontSize: 15, justifySelf: "left" }}
                              >
                                Package: {row.packageName}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: 15,
                                  justifySelf: "left",
                                  mt: 2,
                                }}
                              >
                                Type: {row.recurringOption || selectedRecurringType}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: 15,
                                  justifySelf: "left",
                                  mt: 2,
                                }}
                              >
                                Price: Rs.{" "}
                                {new Intl.NumberFormat("en-US", {
                                  style: "decimal",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(row.price)}
                              </Typography>
                              {recurringOption === 1 ? (
                                <>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontSize: 15, mt: 2 }}
                                  >
                                    <Grid2 container textAlign="start">
                                      <Grid2 item size={{ xs: 4, md: 3 }}>
                                        Reserved Dates:
                                      </Grid2>
                                      <Grid2 item size={{ xs: 8, md: 8 }}>
                                        {" "}
                                        {renderDates()}
                                      </Grid2>
                                    </Grid2>
                                  </Typography>
                                </>
                              ) : (
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: 15,
                                    justifySelf: "left",
                                    mt: 2,
                                  }}
                                >
                                  Date: {row.selectedDate}
                                </Typography>
                              )}
                              <Box sx={{ mt: 2, justifySelf: "right", mr: 1 }}>
                                <Button
                                  variant="text"
                                  color="secondary"
                                  onClick={() =>
                                    navigate("/", {
                                      state: { scrollTo: "packages-preview" },
                                    })
                                  }
                                >
                                  Change Package
                                </Button>
                              </Box>
                            </Box>
                          </Grid2>
                        ))}
                        {recurringOption === 1 && (
                          <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
                            The payment will be charged for the first date only.
                            When your upcoming date is around, a payment link
                            will be sent with the Reminder.
                          </Typography>
                        )}

                        <Alert
                          severity="warning"
                          icon={<ErrorTwoToneIcon fontSize="inherit" />}
                          sx={{
                            mt: { md: 4, xs: 4 },
                            ml: { md: 5, xs: 2 },
                            mr: { xs: 2 },
                            mb: { xs: 2 },
                          }}
                        >
                          Current package prices are only valid for this year.
                        </Alert>
                        <Box sx={{ mt: 4, ml: { md: 5, xs: 2 }, mr: { xs: 2 } }}>
                          <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{
                              bgcolor: "#8B2757",
                              textTransform: "none",
                              fontWeight: "bold",
                              fontSize: 17,
                              py: 1.5,
                              boxShadow: 3,
                              "&:hover": { bgcolor: "#7a2049" },
                            }}
                            onClick={handleStep1Next}
                          >
                            Continue to Donor Details
                          </Button>
                        </Box>
                      </Grid2>
                    </>
                  )}
                </Grid2>
              </Card>
            </Grid2>
            {activeStep === 1 && (
              <Grid2
                item
                size={{ md: 8.2, xs: 10 }}
                mt={7}
                id="billing-details-section"
              >
                <Typography variant="h5" sx={{ fontWeight: "medium", mb: 5 }}>
                  Donor Details
                </Typography>
                <Card>
                  <Grid2 container size={{ xs: 12 }} spacing={2} sx={{ p: 3 }}>
                    <Grid2 item size={{ md: 2.5, xs: 12 }}>
                      <Autocomplete
                        options={titleOptions}
                        getOptionLabel={(option) => option.title || ""}
                        value={
                          titleOptions.find(
                            (option) => option.title === prfTitle,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Title"
                            variant="outlined"
                            fullWidth
                            required
                            sx={{ mb: 1 }}
                            error={touched.prfTitle && formErrors.prfTitle}
                            helperText={
                              touched.prfTitle && formErrors.prfTitle
                                ? "This field is required"
                                : ""
                            }
                            onBlur={() => handleBlur("prfTitle")}
                          />
                        )}
                        onChange={(e, newValue) => {
                          setPrfTitle(newValue?.title || "");
                          setFormErrors((prev) => ({
                            ...prev,
                            prfTitle: !newValue?.title,
                          }));
                        }}
                      />
                    </Grid2>

                    <Grid2 item size={{ md: 9.5, xs: 12 }}>
                      <TextField
                        label="Full Name"
                        variant="outlined"
                        fullWidth
                        required
                        sx={{ mb: 1 }}
                        value={fullName}
                        error={touched.fullName && formErrors.fullName}
                        helperText={
                          touched.fullName && formErrors.fullName
                            ? "This field is required"
                            : ""
                        }
                        onBlur={() => handleBlur("fullName")}
                        onChange={(e) => {
                          setFullName(e.target.value);
                          setFormErrors((prev) => ({
                            ...prev,
                            fullName: !e.target.value,
                          }));
                        }}
                      />
                    </Grid2>

                    <Grid2 item size={{ md: 6, xs: 12 }}>
                      <Autocomplete
                        options={countryOptions}
                        getOptionLabel={(option) => option.country || ""}
                        value={
                          countryOptions.find(
                            (option) => option.country === country,
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Country"
                            variant="outlined"
                            fullWidth
                            required
                            error={touched.country && formErrors.country}
                            helperText={
                              touched.country && formErrors.country
                                ? "This field is required"
                                : ""
                            }
                            onBlur={() => handleBlur("country")}
                            sx={{ mb: 1 }}
                          />
                        )}
                        onChange={(e, newValue) => {
                          const selectedCountry = newValue?.country || "";
                          setCountry(selectedCountry);
                          setFormErrors((prev) => ({
                            ...prev,
                            country: !selectedCountry,
                          }));

                          const countryCode = newValue?.country_code || "+94";
                          setPhone(
                            prependCountryCodeSafe(rawPhone, countryCode),
                          );

                          if (sameAsContact) {
                            setWhatsApp(
                              prependCountryCodeSafe(rawPhone, countryCode),
                            );
                          }
                        }}
                      />
                    </Grid2>

                    <Grid2 item size={{ md: 6, xs: 12 }}>
                      <TextField
                        label="Town/City"
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 1 }}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </Grid2>

                    <Grid2 item size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Contact No"
                        variant="outlined"
                        fullWidth
                        required
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        placeholder="123456789"
                        value={rawPhone}
                        error={touched.phone && formErrors.phone}
                        helperText={
                          touched.phone && formErrors.phone
                            ? "Invalid phone number"
                            : ""
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {countryOptions.find((c) => c.country === country)
                                ?.country_code || "+94"}
                            </InputAdornment>
                          ),
                        }}
                        onBlur={() => {
                          handleBlur("phone");
                          const countryCode =
                            countryOptions.find((c) => c.country === country)
                              ?.country_code || "+94";

                          const formatted = prependCountryCodeSafe(
                            rawPhone,
                            countryCode,
                          );
                          setPhone(formatted);

                          if (sameAsContact) setWhatsApp(formatted);

                          const isoCode =
                            countryOptions.find((c) => c.country === country)
                              ?.iso || "US";
                          setFormErrors((prev) => ({
                            ...prev,
                            phone: !validatePhoneNumber(formatted, isoCode),
                          }));
                        }}
                        onChange={(e) => {
                          // Remove every character except 0–9
                          const numericValue = e.target.value.replace(
                            /\D/g,
                            "",
                          );

                          // Remove leading zeros
                          const input = numericValue.replace(/^0+/, "");

                          setRawPhone(input);

                          const selectedCountry = countryOptions.find(
                            (c) => c.country === country,
                          );

                          const countryCode =
                            selectedCountry?.country_code || "+94";
                          const isoCode = selectedCountry?.iso || "LK";

                          const formattedInput = prependCountryCodeSafe(
                            input,
                            countryCode,
                          );

                          if (sameAsContact) {
                            setWhatsApp(formattedInput);
                          }

                          setFormErrors((prev) => ({
                            ...prev,
                            phone: input
                              ? !validatePhoneNumber(formattedInput, isoCode)
                              : true,
                          }));
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sameAsContact}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSameAsContact(checked);
                              if (checked) {
                                const countryCode =
                                  countryOptions.find(
                                    (c) => c.country === country,
                                  )?.country_code || "+94";
                                const formatted = prependCountryCodeSafe(
                                  rawPhone,
                                  countryCode,
                                );
                                setWhatsApp(formatted);
                              }
                            }}
                          />
                        }
                        label="Same Number for WhatsApp"
                      />
                    </Grid2>

                    <Grid2 item size={{ md: 6, xs: 12 }}>
                      <TextField
                        label="WhatsApp No"
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 1 }}
                        value={whatsApp}
                        onChange={(e) => setWhatsApp(e.target.value)}
                        disabled={sameAsContact}
                      />
                    </Grid2>

                    <Grid2 item size={{ md: 12, xs: 12 }}>
                      <TextField
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        value={email}
                        fullWidth
                        required
                        sx={{ mb: 1 }}
                        error={touched.email && formErrors.email}
                        helperText={
                          touched.email && formErrors.email
                            ? formErrors.email
                            : ""
                        }
                        onBlur={() => {
                          handleBlur("email");

                          setFormErrors((prev) => ({
                            ...prev,
                            email: !email.trim()
                              ? "This field is required"
                              : !validateEmail(email.trim())
                                ? "Invalid email format"
                                : "",
                          }));
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEmail(value);

                          setFormErrors((prev) => ({
                            ...prev,
                            email: !value.trim()
                              ? "This field is required"
                              : !validateEmail(value.trim())
                                ? "Invalid email format"
                                : "",
                          }));
                        }}
                      />
                    </Grid2>

                    <Grid2 item size={{ xs: 12 }}>
                      <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontSize: 15 }}>
                          <Typography
                            component="span"
                            sx={{ fontWeight: "bold" }}
                          >
                            Important:
                          </Typography>{" "}
                          Please provide a concise and meaningful message to
                          accompany your sponsorship. This message will be
                          included in official acknowledgements and
                          communications to recognize your valuable support.
                        </Typography>
                      </Alert>

                      <TextField
                        required
                        label="Reason for Sponsoring"
                        placeholder="Ex: In memory of [Name], celebrating their spirit and legacy."
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ mb: 1 }}
                        value={message}
                        error={touched.message && formErrors.message}
                        helperText={
                          touched.message && formErrors.message
                            ? "This field is required"
                            : ""
                        }
                        onBlur={() => handleBlur("message")}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          setFormErrors((prev) => ({
                            ...prev,
                            message: !e.target.value,
                          }));
                        }}
                      />
                    </Grid2>

                    <Grid2 item size={{ xs: 12 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          justifyContent: "space-between",
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{ bgcolor: "#ffffff", color: "#8B2757" }}
                          onClick={handleStepBack}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          sx={{ bgcolor: "#8B2757" }}
                          onClick={handleStep2Next}
                        >
                          Continue to Donation Method
                        </Button>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Card>
              </Grid2>
            )}

            {activeStep === 2 && (
              <Grid2 item size={{ md: 8.2, xs: 10 }}>
                <Box mt={10}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "medium", mt: 5, mb: 5 }}
                  >
                    Your Donation Method
                  </Typography>
                </Box>
                <Card sx={{ mt: 5, bgcolor: "#EBE5EF" }}>
                  <Box p={5}>
                    <RadioGroup
                      value={selectedOption}
                      onChange={(event) =>
                        setSelectedOption(event.target.value)
                      }
                    >
                      {paymentMethods.map((method) => (
                        <FormControlLabel
                          key={method.id}
                          value={method.id.toString()}
                          control={<Radio />}
                          label={
                            <>
                              {method.type} ({method.option})
                            </>
                          }
                          sx={{ color: "#8B2757" }}
                        />
                      ))}
                      {selectedOption === "1" && (
                        <Box
                          sx={{
                            bgcolor: "#DCD7E3",
                            p: 2,
                            borderRadius: 2,
                            mt: 1,
                            ml: 4,
                          }}
                        >
                          <Typography sx={{ fontSize: 15 }}>
                            {messages.paynow}
                          </Typography>
                        </Box>
                      )}
                      {selectedOption === "2" && (
                        <Box
                          sx={{
                            bgcolor: "#DCD7E3",
                            p: 2,
                            borderRadius: 2,
                            mt: 1,
                            ml: 4,
                          }}
                        >
                          <Typography sx={{ fontSize: 15 }}>
                            {messages.paylater}
                          </Typography>
                        </Box>
                      )}
                    </RadioGroup>
                  </Box>
                  <Divider />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                      px: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        marginY: 2,
                        bgcolor: "#ffffff",
                        width: { md: "55rem" },
                        color: "#8B2757",
                      }}
                      onClick={handleStepBack}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        marginY: 2,
                        bgcolor: "#8B2757",
                        width: { md: "55rem" },
                      }}
                      onClick={handleStepForwardFromPayment}
                      disabled={isSubmitBtnLoading}
                    >
                      Reserve
                    </Button>
                  </Box>
                </Card>
              </Grid2>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 10 }}>
        <Footer />
      </Box>
      <HelpFloatingButton />
    </Box>
  );
};

export default Index;
